import type { CreatePaymentRequestInput } from 'api/paymentRequestsApi';

/** Common DE/AT rates; any other rate can be typed in. */
export const VAT_OPTIONS = ['19', '7', '0', '20', '13', '10'];

export type LineDraft = {
  description: string;
  quantity: string;
  unitPrice: string;
  vatRate: string;
};

export type FormState = {
  kind: 'deposit' | 'payment';
  purpose: string;
  message: string;
  mode: 'amount' | 'lines';
  amount: string;
  vatRate: string;
  lines: LineDraft[];
  payerSource: 'reservation' | 'other';
  payerName: string;
  payerCompany: string;
  payerEmail: string;
  payerPhone: string;
  payerLanguage: 'de' | 'en';
  /** `datetime-local` value; empty means the default (24 h, at most the reservation end). */
  expiresAt: string;
  channel: 'none' | 'email' | 'sms';
};

export const emptyLine = (): LineDraft => ({
  description: '',
  quantity: '1',
  unitPrice: '',
  vatRate: '19',
});

export const emptyForm = (): FormState => ({
  kind: 'deposit',
  purpose: '',
  message: '',
  mode: 'amount',
  amount: '',
  vatRate: '19',
  lines: [emptyLine()],
  payerSource: 'reservation',
  payerName: '',
  payerCompany: '',
  payerEmail: '',
  payerPhone: '',
  payerLanguage: 'de',
  expiresAt: '',
  channel: 'none',
});

/** "49,50" / "49.5" / "49" -> cents; anything else -> null. */
export const euroToCents = (value: string): number | null => {
  const text = (value ?? '').trim();
  if (!/^\d{1,7}(?:[.,]\d{1,2})?$/.test(text)) return null;
  const [euros, cents = ''] = text.replace(',', '.').split('.');
  return Number(euros) * 100 + Number(cents.padEnd(2, '0'));
};

export const normalizeVatRate = (value: string): string | null => {
  const text = (value ?? '').trim().replace(',', '.');
  return /^\d{1,2}(?:\.\d{1,2})?$/.test(text) ? String(Number(text)) : null;
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Errors = Record<string, string>;
export type FormResult =
  | { ok: true; input: CreatePaymentRequestInput }
  | { ok: false; errors: Errors };

const draftLines = (form: FormState): LineDraft[] =>
  form.mode === 'amount'
    ? [
        {
          description: form.purpose,
          quantity: '1',
          unitPrice: form.amount,
          vatRate: form.vatRate,
        },
      ]
    : form.lines;

/** Gross preview only; VAT and the authoritative total come from the API. */
export const previewTotalCents = (form: FormState) =>
  draftLines(form).reduce((sum, line) => {
    const cents = euroToCents(line.unitPrice);
    const quantity = Number(line.quantity);
    return cents !== null && Number.isInteger(quantity)
      ? sum + cents * quantity
      : sum;
  }, 0);

export const formToInput = (
  form: FormState,
  clientRequestId: string
): FormResult => {
  const errors: Errors = {};
  const purpose = form.purpose.trim();
  if (!purpose || purpose.length > 120) errors.purpose = 'Purpose required';

  const lines = draftLines(form).map((line, index) => {
    const prefix = form.mode === 'amount' ? '' : `lines.${index}.`;
    const unitPriceCents = euroToCents(line.unitPrice);
    const quantity = Number(line.quantity);
    const vatRate = normalizeVatRate(line.vatRate);
    const description = line.description.trim();
    if (form.mode === 'lines' && !description)
      errors[`${prefix}description`] = 'Description required';
    if (!unitPriceCents)
      errors[form.mode === 'amount' ? 'amount' : `${prefix}unitPrice`] =
        'Invalid amount';
    if (!/^\d{1,3}$/.test(line.quantity.trim()) || quantity < 1)
      errors[`${prefix}quantity`] = 'Invalid quantity';
    if (vatRate === null)
      errors[form.mode === 'amount' ? 'vatRate' : `${prefix}vatRate`] =
        'Invalid VAT rate';
    return {
      description: description || purpose,
      quantity,
      unitPriceCents: unitPriceCents ?? 0,
      vatRate: vatRate ?? '',
    };
  });

  const other = form.payerSource === 'other';
  if (other && !form.payerName.trim()) errors.payerName = 'Payer name required';
  if (other && form.payerCompany.trim().length > 120)
    errors.payerCompany = 'Company name is too long';
  if (form.payerEmail.trim() && !EMAIL.test(form.payerEmail.trim()))
    errors.payerEmail = 'Invalid email';
  if (other && form.channel === 'email' && !EMAIL.test(form.payerEmail.trim()))
    errors.payerEmail = 'Invalid email';
  if (other && form.channel === 'sms') {
    const phone = form.payerPhone.replace(/[^+0-9]/g, '');
    if (!phone) errors.payerPhone = 'Phone required';
    else if (!/^\+[1-9]\d{5,14}$/.test(phone))
      errors.payerPhone = 'Invalid phone number';
  }

  let expiresAt: number | null = null;
  if (form.expiresAt.trim()) {
    const parsed = Date.parse(form.expiresAt);
    if (!Number.isFinite(parsed)) errors.expiresAt = 'Invalid expiry';
    else expiresAt = parsed;
  }

  if (Object.keys(errors).length) return { ok: false, errors };
  return {
    ok: true,
    input: {
      clientRequestId,
      kind: form.kind,
      purpose,
      message: form.message.trim(),
      ...(other && form.payerCompany.trim()
        ? {
            invoiceRecipient: {
              name: form.payerName.trim(),
              company: form.payerCompany.trim(),
              email: form.payerEmail.trim(),
            },
          }
        : {}),
      payer: other
        ? {
            useReservationGuest: false,
            name: form.payerName.trim(),
            email: form.payerEmail.trim(),
            phone: form.payerPhone.trim(),
            language: form.payerLanguage,
          }
        : { useReservationGuest: true },
      lines,
      expiresAt,
      channel: form.channel,
    },
  };
};

export const refundAllocations = (
  draft: Record<number, string>,
  refundable: { lineIndex: number; remainingCents: number }[]
):
  | {
      ok: true;
      allocations: { lineIndex: number; amountCents: number }[];
      totalCents: number;
    }
  | { ok: false } => {
  const allocations: { lineIndex: number; amountCents: number }[] = [];
  for (const line of refundable) {
    const text = (draft[line.lineIndex] ?? '').trim();
    if (!text) continue;
    const amountCents = euroToCents(text);
    if (!amountCents || amountCents > line.remainingCents) return { ok: false };
    allocations.push({ lineIndex: line.lineIndex, amountCents });
  }
  if (!allocations.length) return { ok: false };
  return {
    ok: true,
    allocations,
    totalCents: allocations.reduce((sum, a) => sum + a.amountCents, 0),
  };
};

const DAY_MS = 24 * 3_600_000;

/**
 * Preview of the default validity for the summary: 24 hours, at most until
 * the reservation ends. The API computes the binding time on creation.
 */
export const defaultExpiryPreview = (
  reservation: { date: string; time: string; reservationLength?: number },
  now: number
) => {
  const start = Date.parse(`${reservation.date}T${reservation.time}`);
  const end =
    start + (Number(reservation.reservationLength) || 8) * 15 * 60_000;
  return Number.isFinite(end) ? Math.min(now + DAY_MS, end) : now + DAY_MS;
};
